import axios from 'axios'

import { appConfig } from '../../app-config/AppConfig'
import { closeContactUsModal } from '../../components/ContactUs/ContactUsModalSlice'
import { AUTH_REFRESH_PATH } from '../../constants'
import { APP_PATHS } from '../../routes/paths'
import AuthService from '../../services/AuthService'
import tokenService from '../../services/TokenService'
import globalStore from '../../store'

import {
  hideLoaderAction,
  setErrorAction,
  setXlsxValidationErrorsAction,
  showLoaderAction,
} from './actions'
import { CustomAxiosRequestConfig, CustomAxiosResponseConfig } from './types'

let refetchCount = 0

const axiosInstance = axios.create({
  baseURL: appConfig.REACT_APP_API_URL,
  withCredentials: true,
})
axiosInstance.interceptors.request.use((request: CustomAxiosRequestConfig) => {
  request.isShowLoader && showLoaderAction()
  const state = globalStore.getState()
  const token = state.tokens.token
  if (token && !request.url?.includes(AUTH_REFRESH_PATH)) {
    request.headers.Authorization = `Bearer ${token}`
  }

  return request
})

const forceLogout = async () => {
  await AuthService.logout()
  window.location.replace(APP_PATHS.auth)
}

axiosInstance.interceptors.response.use(
  (response: CustomAxiosResponseConfig<unknown>) => {
    response.config.isShowLoader && hideLoaderAction()
    refetchCount = 0 // ✅ reset on success
    return response
  },

  async (error) => {
    error.config?.isShowLoader && hideLoaderAction()
    const originalRequest = error.config

    // ✅ NEVER retry refresh endpoint itself
    if (originalRequest?.url === AUTH_REFRESH_PATH) {
      await forceLogout()
      return Promise.reject(error)
    }

    // ✅ NEVER retry logout endpoint
    if (originalRequest?.url === APP_PATHS.logout) {
      return Promise.reject(error)
    }

    // ✅ BLOCK infinite retries
    if (refetchCount >= 1) {
      await forceLogout()
      return Promise.reject(error)
    }

    // ✅ HANDLE AUTH FAILURE
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      refetchCount++

      try {
        await tokenService.refreshToken()

        const newToken = tokenService.getAccessToken()
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${newToken}`

        refetchCount = 0
        return axiosInstance(originalRequest)
      } catch (err) {
        refetchCount = 0
        await forceLogout()
        return Promise.reject(err)
      }
    }

    // ✅ XLSX validation
    if (error.response?.data?.xlsxErrors?.length) {
      setXlsxValidationErrorsAction(error.response.data.xlsxErrors)
    }

    // ✅ Backend errors
    if ([400, 406, 500].includes(error.response?.status)) {
      const { code, stack, response } = error

      setErrorAction({
        code,
        stack,
        backendResponseData: response?.data,
      })

      closeContactUsModal()
      return Promise.reject(response.data)
    }

    // ✅ Network or other errors
    if (!error.response) {
      // Network offline, server down, CORS issue etc.
      return Promise.reject({ message: 'Network error' })
    }

    return Promise.reject(error)
  },
)

export default axiosInstance
